import {
  PlaybookCategory,
  PlaybookSortDirection,
  PlaybookSortProperty,
} from '@driftt/api-types'
import {
  Button,
  CardContainer,
  Div,
  H1,
  Input,
  NavigationHeader,
  P,
} from '@driftt/tide-core'
import { UploadArea } from '@driftt/tide-file-upload'
import { Select } from '@driftt/tide-selects'
import { showTransientNotification } from '@driftt/traffic-control'
import debounce from 'lodash/debounce'
import { filter, isEmpty, isNil, not, pathOr } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'

import { getBlocksTableData } from 'api/blocks'
import { uploadBlockTranslation } from 'api/blocks'
import { upload } from 'api/file'
import { getPlaybooks, uploadPlaybookTranslation } from 'api/playbooks'

import './style.css'

const TRANSLATION_ITEM_TYPE: Record<ItemType, { key: ItemType; noun: string }> =
  {
    BLOCK: { key: 'BLOCK', noun: 'block' },
    PLAYBOOK: { key: 'PLAYBOOK', noun: 'playbook' },
  }

type ItemType = 'BLOCK' | 'PLAYBOOK'

interface SelectOption {
  label: string
  value: number | string
}

interface FileUploadProps {
  fileUrl: string | null
  fileName: string | null
}

interface FilePreview extends File {
  preview: string
}

const CloneToolLayout = () => {
  const [itemType, setItemType] = useState<ItemType>('PLAYBOOK')
  const [enableGetAllBlocks, setEnableGetAllBlocks] = useState<boolean>(false)
  const [enableGetAllPlaybooks, setEnableGetAllPlaybooks] =
    useState<boolean>(true)
  const [playbookList, setPlaybookList] = useState<SelectOption[] | null>(null)
  const [blockList, setBlockList] = useState<SelectOption[] | null>(null)
  const [selectedItem, setSelectedItem] = useState<number | string | null>(null)
  const [itemName, setSelectedItemName] = useState('')
  const [isOverwriting, setIsOverwriting] = useState(false)
  const [fileToUpload, setFileToUpload] = useState<FileUploadProps>({
    fileUrl: null,
    fileName: null,
  })
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [filePreviewName, setFilePreviewName] = useState<string | null>(null)

  const dispatch = useDispatch()

  const { data: allBlocks } = useQuery(
    'reusable-block--list',
    async () => {
      setEnableGetAllBlocks(false)

      if (blockList) {
        return undefined
      }

      const { data } = await getBlocksTableData({ page: 0, pageSize: 100 })
      return data
    },
    { enabled: enableGetAllBlocks }
  )

  const { data: allPlaybooks } = useQuery(
    'playbook--list',
    async () => {
      setEnableGetAllPlaybooks(false)

      if (playbookList) {
        return undefined
      }

      const { data } = await getPlaybooks({
        includeMetrics: false,
        category: [
          PlaybookCategory.CHAT_ACTIVE,
          PlaybookCategory.FASTLANE_ACTIVE,
        ],
        sortProperty: PlaybookSortProperty.priority,
        nameQuery: '',
        page: 0,
        pageSize: 100,
        onlyMine: false,
        sortDirection: PlaybookSortDirection.asc,
      })
      return data
    },
    { enabled: enableGetAllPlaybooks }
  )

  useEffect(() => {
    if (!!allBlocks && !blockList) {
      const { blockPageRows: blocks } = allBlocks
      setBlockList(
        blocks.map(({ blockId, blockName }) => ({
          label: blockName,
          value: blockId,
        }))
      )
    }
  }, [allBlocks, blockList])

  useEffect(() => {
    if (!!allPlaybooks && !playbookList) {
      const { playbooks } = allPlaybooks
      setPlaybookList(
        playbooks.map(({ playbook: { id, name } }) => ({
          label: name,
          value: id,
        }))
      )
    }
  }, [allPlaybooks, playbookList])

  useEffect(() => {
    if (itemType === TRANSLATION_ITEM_TYPE.BLOCK.key) {
      setEnableGetAllBlocks(true)
    }
    if (itemType === TRANSLATION_ITEM_TYPE.PLAYBOOK.key) {
      setEnableGetAllPlaybooks(true)
    }
  }, [itemType])

  const isItemPlaybook = itemType === TRANSLATION_ITEM_TYPE.PLAYBOOK.key
  const isItemBlock = itemType === TRANSLATION_ITEM_TYPE.BLOCK.key

  const listToFilter = useMemo(() => {
    if (!!blockList && isItemBlock) {
      return blockList
    }

    if (!!playbookList && isItemPlaybook) {
      return playbookList
    }

    return []
  }, [blockList, isItemBlock, playbookList, isItemPlaybook])

  const userCanUpload =
    !isEmpty(selectedItem) &&
    (!isOverwriting ? !isEmpty(itemName) : true) &&
    !isNil(fileToUpload)

  const uploadBlockFile = async () => {
    try {
      const { data } = await uploadBlockTranslation(
        selectedItem as string,
        fileToUpload,
        isOverwriting,
        itemName
      )
      return data
    } catch (e) {
      throw e
    }
  }

  const uploadPlaybookFile = async () => {
    try {
      const { data } = await uploadPlaybookTranslation(
        selectedItem as number,
        fileToUpload,
        isOverwriting,
        itemName
      )
      return data
    } catch (e) {
      throw e
    }
  }

  const handleFileUpload = async () => {
    try {
      let data

      if (isItemBlock) {
        data = await uploadBlockFile()
      }

      if (isItemPlaybook) {
        data = await uploadPlaybookFile()
      }

      if (!!data) {
        dispatch(
          showTransientNotification({
            type: 'success',
            message: `Your ${isOverwriting ? '' : 'new '} ${
              TRANSLATION_ITEM_TYPE[itemType].noun
            } was successfully ${isOverwriting ? 'updated' : 'created'}`,
          })
        )
      }
    } catch (e) {
      console.warn(e)
      dispatch(
        showTransientNotification({
          type: 'error',
          message:
            'Woops, an error has occurred, reach out to support@drift.com for assistance',
        })
      )
    }
  }

  const handleFileChosen = async (file: FilePreview) => {
    setFilePreviewName(file.name)
    setFilePreviewUrl(file.preview)
    const { data: uploadedFile } = await upload(file)
    return uploadedFile
  }

  const handleRadioInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    setItemType(event?.target?.name as ItemType)
  }

  const getPlaybookListByQuery = async (nameQuery: string) => {
    const { data } = await getPlaybooks({
      includeMetrics: false,
      category: [
        PlaybookCategory.CHAT_ACTIVE,
        PlaybookCategory.FASTLANE_ACTIVE,
      ],
      sortProperty: PlaybookSortProperty.priority,
      nameQuery,
      page: 0,
      onlyMine: false,
      sortDirection: PlaybookSortDirection.asc,
    })
    const options = data?.playbooks.map((item) => ({
      label: item?.playbook?.name,
      value: item?.playbook?.id,
    }))
    return options
  }

  const getBlockListByQuery = async (nameQuery: string) => {
    const { data } = await getBlocksTableData({
      nameQuery,
      page: 0,
    })
    const options = data?.blockPageRows.map((item) => ({
      label: item?.blockName,
      value: item?.blockId,
    }))
    return options
  }

  const debouncedPlaybookLoadOptions = useCallback(
    debounce((nameQuery: string, callback: Function) => {
      getPlaybookListByQuery(nameQuery).then((options) => callback(options))
    }, 300),
    []
  )

  const debouncedBlockLoadOptions = useCallback(
    debounce((nameQuery: string, callback: Function) => {
      getBlockListByQuery(nameQuery).then((options) => callback(options))
    }, 300),
    []
  )

  return (
    <Div className="translation-tool-container">
      <NavigationHeader
        title={() => <H1>Translated playbook and block upload</H1>}
      />
      <CardContainer
        header="Upload a translated playbook or block"
        padding="medium"
        boxShadow="--box-shadow-2"
        className="translation-card-container"
      >
        <Div className="translation-tool-row">
          <P className="translation-card-caption">
            Is this a playbook or block?
          </P>
          <Div className="translation-tool-type-picker">
            <label className="translation-tool-type-option">
              <Input
                checked={isItemPlaybook}
                name={TRANSLATION_ITEM_TYPE.PLAYBOOK.key}
                onChange={handleRadioInputChange}
                type="radio"
              />
              Playbook
            </label>
            <label className="translation-tool-type-option">
              <Input
                checked={isItemBlock}
                name={TRANSLATION_ITEM_TYPE.BLOCK.key}
                onChange={handleRadioInputChange}
                type="radio"
              />
              Block
            </label>
          </Div>
        </Div>
        {!!playbookList && isItemPlaybook && (
          <Div className="translation-tool-row">
            <Div className="translation-tool-select">
              <Select
                async
                label="Which playbook is this translated version based on?"
                placeholder="Select one..."
                defaultOptions={playbookList}
                loadOptions={debouncedPlaybookLoadOptions}
                onChange={({ value }: { value: number }) => {
                  setSelectedItem(value)
                }}
              />
            </Div>
          </Div>
        )}
        {!!blockList && isItemBlock && (
          <Div className="translation-tool-row">
            <Div className="translation-tool-select">
              <Select
                async
                label="Which block is this translated version based on?"
                placeholder="Select one..."
                defaultOptions={blockList}
                loadOptions={debouncedBlockLoadOptions}
                onChange={({ value }: { value: number }) => {
                  setSelectedItem(value)
                }}
              />
            </Div>
          </Div>
        )}
        <Div className="translation-tool-check">
          <Input
            type="checkbox"
            checked={isOverwriting}
            onChange={() => setIsOverwriting(not(isOverwriting))}
            label={`Check this box if you wish to overwrite the selected ${TRANSLATION_ITEM_TYPE[itemType].noun} with the contents of this upload`}
          />
        </Div>
        <Div className="translation-tool-row">
          {((!!playbookList && isItemPlaybook) ||
            (!!blockList && isItemBlock)) && (
            <Input
              type="text"
              label={`${TRANSLATION_ITEM_TYPE[itemType].noun} name`}
              placeholder={`Enter a name for the translated ${TRANSLATION_ITEM_TYPE[itemType].noun}`}
              disabled={isOverwriting}
              value={
                isOverwriting
                  ? pathOr(
                      '',
                      ['label'],
                      filter(
                        (item: SelectOption) => item.value === selectedItem,
                        listToFilter
                      )
                    )
                  : itemName
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSelectedItemName(event.target.value)
              }
            />
          )}
        </Div>
        <Div className="translation-tool-row">
          <P className="translation-card-caption">
            {`Upload the translated ${TRANSLATION_ITEM_TYPE[itemType].noun}:`}
          </P>
          <P className="translation-card-upload-note">
            {`Note: you must complete the prior steps first. Once the file is uploaded this creates the new ${TRANSLATION_ITEM_TYPE[itemType].noun}`}
          </P>
          <UploadArea
            className="translation-tool-upload-area"
            type="csv"
            onTypeError={() => {
              showTransientNotification({
                type: 'error',
                message:
                  'This tool only supports CSV uploads, please use a CSV',
              })
            }}
            onReadError={() => {}}
            handleSaveFile={(fileUrl: string, fileName: string) => {
              setFileToUpload({ fileUrl, fileName })
            }}
            handleUploadFile={handleFileChosen}
            previewUrl={filePreviewUrl}
            previewName={filePreviewName}
          />
        </Div>
        <Div className="translation-tool-row">
          <Button
            type="primary"
            onClick={handleFileUpload}
            disabled={!userCanUpload}
          >
            {`Upload translated ${TRANSLATION_ITEM_TYPE[itemType].noun}`}
          </Button>
        </Div>
      </CardContainer>
    </Div>
  )
}

export default CloneToolLayout
